import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, ChevronDown, ChevronUp, MessageCircle, Sparkles, ShieldCheck, BookOpen, ArrowLeft, ArrowRight } from 'lucide-react';
import RelatedTools from '../components/RelatedTools';
import TopicalCrossLinks from '../components/TopicalCrossLinks';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEO from '../components/SEO';
import { useLang } from '../context/LanguageContext';
import {
  trackConsultationCTAClick, trackWhatsAppClick, trackFAQOpen,
  initScrollDepthTracking, resetScrollDepth, trackEvent
} from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

// Feb 2026 CRO — High-traffic FAQ → Poocho Guruji ₹99 in-content CTA map.
// Slug pattern → pre-filled custom question that deep-links straight into the
// Poocho Guruji funnel with a matching sample question already selected.
// This lets high-intent FAQ visitors convert to ₹99 in one tap.
const POOCHO_FAQ_CTAS = [
  { match: /gun-milan|guna-milan|गुण-?मिलान/i, question: 'गुण मिलान 36 से कम है — क्या मेरी शादी फिर भी शुभ होगी?' },
  { match: /career|कैरियर|करियर.*(kundli|कुंडली)|kundli.*(career|कैरियर|करियर)/i, question: 'मेरी कुंडली के अनुसार मुझे किस क्षेत्र में career बनाना चाहिए?' },
  { match: /mangal-dosh.*(kab|kitne|kaise|कब|कितने)|kab-tak.*mangal/i, question: 'मंगल दोष मेरी कुंडली में कब तक रहेगा?' },
  { match: /mangal-dosh.*(vivah|shadi|marriage|विवाह|शादी)|vivah.*mangal|shadi.*mangal/i, question: 'मंगल दोष का मेरे विवाह पर क्या प्रभाव होगा?' },
];

const buildPoochoDeepLink = (faqSlug, faqQuestion) => {
  const rule = POOCHO_FAQ_CTAS.find(r => r.match.test(faqSlug || '') || r.match.test(faqQuestion || ''));
  const q = rule ? rule.question : (faqQuestion || '').slice(0, 200);
  return `/poocho-guruji?custom=${encodeURIComponent(q)}`;
};

const isPoochoCtaFaq = (faqSlug, faqQuestion) =>
  POOCHO_FAQ_CTAS.some(r => r.match.test(faqSlug || '') || r.match.test(faqQuestion || ''));

const buildBookingLink = (lang, contextNote = '') => {
  const phone = '919311369729';
  const msg = lang === 'hi'
    ? `नमस्कार आचार्य Vivek जी 🙏\n\n${contextNote ? contextNote + '\n\n' : ''}मुझे अपनी जन्म कुंडली का गहन Parashari विश्लेषण व व्यक्तिगत परामर्श करवाना है।\n\nकृपया available timing व booking details बताएं।`
    : `Namaste Acharya Vivek ji 🙏\n\n${contextNote ? contextNote + '\n\n' : ''}I want a detailed Parashari analysis and personal consultation. Please share timings and booking details.`;
  return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
};

export default function FAQPage() {
  const { categorySlug, faqSlug } = useParams();
  const { lang } = useLang();
  const [categories, setCategories] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [singleFaq, setSingleFaq] = useState(null);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(categorySlug || 'all');
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    resetScrollDepth();
    initScrollDepthTracking();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (faqSlug) {
      fetchSingleFaq(faqSlug);
    } else {
      setSingleFaq(null);
      setActiveCategory(categorySlug || 'all');
      fetchFaqs(categorySlug || null);
    }
  }, [categorySlug, faqSlug]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/faqs/categories`);
      setCategories(res.data.categories || []);
    } catch (e) { /* silent */ }
  };

  const fetchFaqs = async (cat) => {
    try {
      setLoading(true);
      const url = cat ? `${API}/api/faqs?category=${cat}` : `${API}/api/faqs`;
      const res = await axios.get(url);
      setFaqs(res.data.faqs || []);
    } catch (e) {
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleFaq = async (slug) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/faqs/${slug}`);
      setSingleFaq(res.data);
    } catch (e) {
      setSingleFaq({ error: true });
    } finally {
      setLoading(false);
    }
  };

  const filteredFaqs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return faqs;
    return faqs.filter(f =>
      (f.question_hi || '').toLowerCase().includes(q) ||
      (f.question_en || '').toLowerCase().includes(q) ||
      (f.short_answer_hi || '').toLowerCase().includes(q)
    );
  }, [search, faqs]);

  const handleOpen = (faq) => {
    if (openId === faq.id) { setOpenId(null); return; }
    setOpenId(faq.id);
    trackFAQOpen(faq.slug, faq.category);
  };

  const getQ = (f) => (lang === 'hi' || !f.question_en) ? f.question_hi : f.question_en;
  const getShort = (f) => (lang === 'hi' || !f.short_answer_en) ? f.short_answer_hi : f.short_answer_en;
  const getLong = (f) => (lang === 'hi' || !f.long_answer_en) ? f.long_answer_hi : f.long_answer_en;

  // Build JSON-LD FAQPage schema for Google PAA visibility
  const jsonLd = useMemo(() => {
    const list = (singleFaq && !singleFaq.error) ? [singleFaq] : filteredFaqs;
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: list.map(f => ({
        '@type': 'Question',
        name: getQ(f),
        acceptedAnswer: {
          '@type': 'Answer',
          text: (getShort(f) || '') + (getLong(f) ? ' ' + getLong(f) : ''),
        },
      })),
    };
  }, [filteredFaqs, singleFaq, lang]);

  // ============== SINGLE FAQ DETAIL VIEW ==============
  if (faqSlug) {
    if (loading) {
      return <div className="min-h-screen bg-background"><Header /><div className="container-custom py-32 text-center text-text-muted">{lang === 'hi' ? 'लोड हो रहा है...' : 'Loading...'}</div></div>;
    }
    if (!singleFaq || singleFaq.error) {
      // Feb 12, 2026 (N3 SEO Fix) — invalid FAQ slug must return a proper
      // 404 signal (noindex + prerender-status-code=404), not redirect to
      // hub with 200. Prevents Perplexity "faq/no-such-faq-999 → 200".
      return (
        <div className="min-h-screen bg-background" data-testid="faq-not-found">
          <SEO
            title={lang === 'hi' ? 'FAQ नहीं मिला' : 'FAQ Not Found'}
            description="FAQ not found on ViratAstro"
            path={`/faq/${faqSlug}`}
            noIndex
          />
          <Helmet><meta name="prerender-status-code" content="404" /></Helmet>
          <Header />
          <div className="container-custom py-32 text-center">
            <h1 className="font-heading text-3xl text-text-main mb-4">
              {lang === 'hi' ? 'यह FAQ नहीं मिला' : 'FAQ Not Found'}
            </h1>
            <p className="text-text-muted mb-6">{lang === 'hi' ? 'यह FAQ उपलब्ध नहीं है।' : 'This FAQ is not available.'}</p>
            <Link to="/faq" className="text-gold hover:underline">← {lang === 'hi' ? 'सभी FAQs देखें' : 'View All FAQs'}</Link>
          </div>
          <Footer />
        </div>
      );
    }
    const f = singleFaq;
    return (
      <div className="min-h-screen bg-background">
        <SEO
          title={getQ(f)}
          description={getShort(f)?.slice(0, 160)}
          path={`/faq/${f.slug}`}
          breadcrumbs={[
            { name: lang === 'hi' ? 'होम' : 'Home', path: '/' },
            { name: lang === 'hi' ? 'FAQs' : 'FAQs', path: '/faq' },
            { name: getQ(f), path: `/faq/${f.slug}` },
          ]}
          keywords={`${f.category}, ${getQ(f)}, Vivek Mudgal, FAQ, Vedic Astrology`}
          jsonLd={jsonLd}
        />
        <Header />
        <FloatingActions />
        <section className="pt-32 pb-12 cosmic-bg">
          <div className="container-custom max-w-4xl">
            <Link to="/faq" className="inline-flex items-center gap-2 text-primary hover:gap-3 transition-all mb-6 text-sm" data-testid="back-faq-btn">
              <ArrowLeft className="w-4 h-4" /> {lang === 'hi' ? 'सभी FAQs' : 'All FAQs'}
            </Link>
            <span className="block text-xs tracking-[0.3em] uppercase text-gold font-semibold mb-3">
              {lang === 'hi' ? 'FAQ — आचार्य Vivek Mudgal' : 'FAQ — Acharya Vivek Mudgal'}
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl text-text-main mb-6 leading-tight" data-testid="faq-title">{getQ(f)}</h1>
            <div className="bg-gold/10 border-l-4 border-gold rounded-r-xl p-5 mb-8">
              <p className="text-text-main leading-relaxed text-base sm:text-lg" data-testid="faq-short-answer">{getShort(f)}</p>
            </div>
            {getLong(f) && (
              <div className="text-text-main leading-[1.9] text-base whitespace-pre-line mb-8" data-testid="faq-long-answer">{getLong(f)}</div>
            )}
            {f.shloka && (
              <div className="mb-8 bg-gradient-to-br from-amber-900/20 to-orange-900/10 border-l-4 border-gold rounded-r-xl p-5" data-testid="faq-shloka">
                <div className="text-xs uppercase tracking-widest text-gold mb-2 font-semibold">🕉️ {lang === 'hi' ? 'पराशरीय श्लोक' : 'Parashari Shloka'}</div>
                <p className="text-gold font-heading text-lg leading-relaxed mb-2 italic whitespace-pre-line">{f.shloka}</p>
                {f.shloka_meaning && <p className="text-sm text-text-muted leading-relaxed">— {f.shloka_meaning}</p>}
                {f.shloka_source && <p className="text-xs text-gold/70 mt-2 font-medium">📿 {f.shloka_source}</p>}
              </div>
            )}
            {f.guruji_note && (
              <div className="mb-8 bg-gradient-to-br from-purple-900/30 to-rose-900/10 border border-purple-500/30 rounded-2xl p-5" data-testid="faq-guruji-note">
                <div className="text-xs uppercase tracking-widest text-purple-300 mb-2 font-semibold">💬 {lang === 'hi' ? 'आचार्य Vivek जी का अनुभव' : "Acharya Vivek's Experience"}</div>
                <p className="text-text-main italic leading-relaxed">{f.guruji_note}</p>
              </div>
            )}
            {f.upay && (
              <div className="mb-8 bg-gold/10 border border-gold/30 rounded-xl p-5" data-testid="faq-upay">
                <div className="flex items-center gap-2 text-gold mb-2 font-semibold"><Sparkles className="w-5 h-5" /> {lang === 'hi' ? 'विशेष वैदिक उपाय' : 'Special Vedic Remedy'}</div>
                <p className="text-text-main leading-relaxed whitespace-pre-line">{f.upay}</p>
              </div>
            )}
            {/* Feb 2026 CRO — High-traffic FAQ → Poocho Guruji ₹99 in-content CTA
                Only for target FAQs: gun-milan, career-kundli, mangal-dosh timing/marriage */}
            {isPoochoCtaFaq(f.slug, getQ(f)) && (
              <div className="mb-8 relative overflow-hidden rounded-2xl border border-gold/45 bg-gradient-to-br from-[#1a1230] via-[#0f0b25] to-[#0a0820] p-5 sm:p-6" data-testid={`faq-poocho-99-cta-${f.slug}`}>
                <div className="absolute -top-8 -right-8 w-32 h-32 bg-gold/15 rounded-full blur-3xl pointer-events-none" />
                <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-gold" />
                      <span className="text-[10.5px] uppercase tracking-[0.25em] text-gold font-semibold">₹99 · तुरंत उत्तर</span>
                    </div>
                    <h4 className="font-heading text-lg sm:text-xl text-text-main leading-snug mb-1">
                      {lang === 'hi' ? 'अपनी कुंडली पर यही सवाल पूछें' : 'Ask this question on YOUR chart'}
                    </h4>
                    <p className="text-text-muted text-[13px] leading-relaxed">
                      {lang === 'hi'
                        ? 'ऊपर का उत्तर सामान्य है — पर आपकी कुंडली अलग है। ₹99 में आचार्य Vivek जी की Parashari AI आपको WhatsApp पर व्यक्तिगत उत्तर देगी।'
                        : 'The above answer is general — YOUR chart is different. For ₹99, Acharya Vivek ji\'s Parashari AI gives you a personalised WhatsApp answer.'}
                    </p>
                  </div>
                  <Link
                    to={buildPoochoDeepLink(f.slug, getQ(f))}
                    onClick={() => {
                      try {
                        trackEvent('faq_poocho_99_cta_click', {
                          faq_slug: f.slug,
                          faq_category: f.category,
                          tier: 'quick',
                          price_inr: 99,
                          source: 'faq_in_content',
                        });
                      } catch (_) { /* noop */ }
                    }}
                    className="shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-[#F4D477] via-[#E5C158] to-[#B8942D] text-[#0a0820] font-bold text-sm shadow-lg hover:scale-105 active:scale-95 transition-transform whitespace-nowrap"
                    data-testid={`faq-poocho-99-btn-${f.slug}`}
                  >
                    {lang === 'hi' ? '₹99 में उत्तर पाएँ' : 'Get answer for ₹99'}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            )}

            <ConsultationCTA lang={lang} contextNote={lang === 'hi' ? `मेरा सवाल था: ${getQ(f)}` : `My question was: ${getQ(f)}`} />
          </div>
        </section>
        {/* Feb 12, 2026 SEO Fix — Topical cross-links from FAQ → Blog/Upay/Tool
            to strengthen internal linking for "Discovered - not indexed" pages. */}
        <TopicalCrossLinks hint={`${f.category || ''} ${f.slug || ''} ${getQ(f) || ''}`} lang={lang} />
        <RelatedTools />
        <Footer />
      </div>
    );
  }

  // ============== MASTER FAQ LIST VIEW ==============
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={lang === 'hi' ? 'अक्सर पूछे जाने वाले प्रश्न (FAQs)' : 'Frequently Asked Questions (FAQs)'}
        breadcrumbs={[
          { name: lang === 'hi' ? 'होम' : 'Home', path: '/' },
          { name: lang === 'hi' ? 'FAQs' : 'FAQs', path: '/faq' },
        ]}
        description={lang === 'hi'
          ? 'आचार्य Vivek Mudgal द्वारा वैदिक ज्योतिष पर 33+ वर्षों के अनुभव से प्रामाणिक उत्तर — मांगलिक दोष, कुंडली, गुण मिलान, वास्तु, रत्न, करियर, अंकशास्त्र, उपाय।'
          : 'Authentic answers on Vedic astrology by Acharya Vivek Mudgal (33+ years experience) — Mangal Dosha, Kundli, Gun Milan, Vastu, Gemstones, Career, Numerology, Remedies.'}
        path="/faq"
        keywords="FAQ, vedic astrology questions, मांगलिक दोष, कुंडली, गुण मिलान, वास्तु, रत्न, ज्योतिष FAQ, Vivek Mudgal"
        jsonLd={jsonLd}
      />
      <Header />
      <FloatingActions />

      {/* Hero */}
      <section className="pt-32 pb-8 cosmic-bg">
        <div className="container-custom text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-primary hover:gap-3 transition-all mb-6 text-sm" data-testid="back-home-btn">
            <ArrowLeft className="w-4 h-4" /> {lang === 'hi' ? 'होम' : 'Home'}
          </Link>
          <span className="block text-xs tracking-[0.3em] uppercase text-gold font-semibold mb-4">
            {lang === 'hi' ? 'पराशरीय ज्योतिष FAQs' : 'PARASHARI ASTROLOGY FAQs'}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-text-main mb-3">
            {lang === 'hi' ? 'अक्सर पूछे जाने वाले प्रश्न' : 'Frequently Asked Questions'}
          </h1>
          <p className="text-text-muted max-w-2xl mx-auto mb-6">
            {lang === 'hi'
              ? 'आचार्य Vivek Mudgal के 33+ वर्षों के अनुभव से प्रामाणिक उत्तर — पराशर शास्त्र के सिद्धांतों पर आधारित।'
              : "Authentic answers from Acharya Vivek Mudgal's 33+ years of experience — rooted in Maharshi Parashar principles."}
          </p>

          {/* Search */}
          <div className="max-w-xl mx-auto relative" data-testid="faq-search">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={lang === 'hi' ? 'सवाल खोजें... (e.g., मांगलिक दोष)' : 'Search questions... (e.g., mangal dosha)'}
              className="w-full bg-white/10 border border-white/20 rounded-full pl-12 pr-5 py-3.5 text-text-main placeholder-text-muted focus:border-gold/40 focus:outline-none"
              data-testid="faq-search-input" />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="container-custom -mt-2 mb-8">
        <div className="flex gap-2 overflow-x-auto pb-2 sm:flex-wrap sm:justify-center" data-testid="faq-categories">
          <CategoryPill
            active={activeCategory === 'all'}
            onClick={() => { setActiveCategory('all'); fetchFaqs(null); window.history.replaceState(null, '', '/faq'); }}
            icon="📚" label={lang === 'hi' ? 'सभी' : 'All'}
            count={faqs.length}
            testId="faq-cat-all"
          />
          {categories.map(c => (
            <CategoryPill
              key={c.slug}
              active={activeCategory === c.slug}
              onClick={() => { setActiveCategory(c.slug); fetchFaqs(c.slug); window.history.replaceState(null, '', `/faq?category=${c.slug}`); }}
              icon={c.icon} label={lang === 'hi' ? c.title_hi : c.title_en}
              count={c.count}
              testId={`faq-cat-${c.slug}`}
            />
          ))}
        </div>
      </section>

      {/* FAQ List - Accordion */}
      <section className="container-custom max-w-4xl pb-8">
        {loading ? (
          <div className="text-center text-text-muted py-12">{lang === 'hi' ? 'प्रश्न लोड हो रहे हैं...' : 'Loading questions...'}</div>
        ) : filteredFaqs.length === 0 ? (
          <div className="text-center text-text-muted py-12">{lang === 'hi' ? 'कोई प्रश्न नहीं मिला' : 'No questions found'}</div>
        ) : (
          <div className="space-y-3" data-testid="faq-list">
            {filteredFaqs.map(f => (
              <div key={f.id} className="glass-card overflow-hidden" data-testid={`faq-item-${f.slug}`}>
                <button
                  onClick={() => handleOpen(f)}
                  className="w-full flex items-start sm:items-center gap-3 p-5 text-left hover:bg-white/5 transition-colors"
                  data-testid={`faq-toggle-${f.slug}`}>
                  <span className="text-gold font-heading text-xl mt-0.5 sm:mt-0">Q.</span>
                  <span className="flex-1 font-heading text-base sm:text-lg text-text-main">{getQ(f)}</span>
                  {openId === f.id ? <ChevronUp className="w-5 h-5 text-gold" /> : <ChevronDown className="w-5 h-5 text-text-muted" />}
                </button>
                {openId === f.id && (
                  <div className="px-5 pb-5 border-t border-white/10 pt-4">
                    <div className="bg-gold/5 border-l-2 border-gold/40 rounded-r-lg p-4 mb-3">
                      <p className="text-text-main leading-relaxed">{getShort(f)}</p>
                    </div>
                    {f.shloka && (
                      <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/10 border-l-2 border-gold/60 rounded-r-lg p-3 mb-3">
                        <p className="text-gold font-heading text-sm leading-relaxed mb-1 italic whitespace-pre-line">🕉️ {f.shloka}</p>
                        {f.shloka_source && <p className="text-[11px] text-gold/70 mt-1">📿 {f.shloka_source}</p>}
                      </div>
                    )}

                    <Link
                      to={`/faq/${f.slug}`}
                      className="inline-flex items-center gap-1 text-sm text-primary hover:gap-2 transition-all font-medium mb-4"
                      data-testid={`faq-readmore-${f.slug}`}>
                      {lang === 'hi' ? 'पूरा उत्तर + Guruji का अनुभव + उपाय पढ़ें →' : 'Read full answer + Guruji insight + remedy →'}
                    </Link>

                    {/* Feb 2026 CRO — inline ₹99 Poocho Guruji shortcut for high-traffic FAQs */}
                    {isPoochoCtaFaq(f.slug, getQ(f)) && (
                      <Link
                        to={buildPoochoDeepLink(f.slug, getQ(f))}
                        onClick={() => {
                          try {
                            trackEvent('faq_poocho_99_cta_click', {
                              faq_slug: f.slug,
                              faq_category: f.category,
                              tier: 'quick',
                              price_inr: 99,
                              source: 'faq_accordion',
                            });
                          } catch (_) { /* noop */ }
                        }}
                        className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-gold/40 bg-gradient-to-r from-[#1a1230]/60 to-[#0a0820]/60 px-4 py-3 hover:border-gold transition-all group"
                        data-testid={`faq-poocho-99-inline-${f.slug}`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <Sparkles className="w-4 h-4 text-gold shrink-0" />
                          <span className="text-sm text-text-main truncate">
                            {lang === 'hi' ? 'अपनी कुंडली पर तुरंत उत्तर — ₹99' : 'Get YOUR chart answer — ₹99'}
                          </span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gold group-hover:translate-x-1 transition-transform shrink-0" />
                      </Link>
                    )}

                    {/* Personalized Consultation CTA — Guruji's voice, human-feeling */}
                    <div className="relative overflow-hidden rounded-2xl border border-gold/30 bg-gradient-to-br from-purple-950/60 via-purple-900/40 to-amber-950/30 p-5 sm:p-6 mt-3" data-testid={`faq-cta-block-${f.slug}`}>
                      <div className="absolute -top-10 -right-10 w-40 h-40 bg-gold/8 rounded-full blur-3xl pointer-events-none" />

                      {/* Acharya avatar + intro line */}
                      <div className="relative flex items-start gap-3 mb-3">
                        <div className="shrink-0 w-11 h-11 rounded-full bg-gradient-to-br from-gold/40 to-amber-700/40 border-2 border-gold/50 flex items-center justify-center text-xl shadow-lg">
                          🕉️
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] uppercase tracking-[0.2em] text-gold/80 font-semibold">
                            {lang === 'hi' ? 'आचार्य विवेक मुदगल' : 'Acharya Vivek Mudgal'}
                          </div>
                          <div className="text-[11px] text-text-muted">
                            {lang === 'hi' ? '33+ वर्षों का अनुभव • फरीदाबाद' : '33+ years experience • Faridabad'}
                          </div>
                        </div>
                      </div>

                      {/* Personal note */}
                      <div className="relative text-text-main text-[15px] leading-relaxed mb-4 pl-1">
                        <p className="mb-2">
                          {lang === 'hi'
                            ? <>ऊपर जो उत्तर है वो सामान्य है 🙏 — पर सच यह है कि <span className="text-gold font-semibold">हर कुंडली अलग होती है</span>। आपके मंगल की डिग्री, नक्षत्र, दृष्टि, दशा-काल — ये सब मिलकर ही आपका सटीक उत्तर देते हैं।</>
                            : <>The answer above is general 🙏 — but the truth is, <span className="text-gold font-semibold">every chart is unique</span>. Your Mars degree, nakshatra, aspects, dasha period — only together they give YOUR real answer.</>}
                        </p>
                        <p className="text-text-muted text-[14px]">
                          {lang === 'hi'
                            ? 'यदि यह विषय वाक़ई आपके जीवन को प्रभावित कर रहा है — तो एक बार बैठ कर बात कर लेते हैं।'
                            : "If this issue is genuinely affecting your life — let's sit and talk it through, one chart at a time."}
                        </p>
                      </div>

                      {/* Trust strip */}
                      <div className="relative flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-text-muted mb-4 pl-1">
                        <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-gold/70" /> {lang === 'hi' ? '33,000+ कुंडलियाँ देखी हैं' : '33,000+ charts read'}</span>
                        <span className="flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5 text-gold/70" /> {lang === 'hi' ? 'पराशर शास्त्र आधारित' : 'Parashari Shastra based'}</span>
                        <span className="flex items-center gap-1.5">📿 {lang === 'hi' ? 'कोई automated report नहीं' : 'No automated reports'}</span>
                      </div>

                      {/* Action row */}
                      <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-white/10">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-gold/70 font-semibold">{lang === 'hi' ? 'व्यक्तिगत परामर्श — दक्षिणा' : 'Personal Consultation Dakshina'}</div>
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-heading text-gold leading-none">₹5,100</span>
                            <span className="text-[11px] text-text-muted">{lang === 'hi' ? '• 45–60 मिनट सीधे बात' : '• 45–60 min direct call'}</span>
                          </div>
                        </div>
                        <a
                          href={buildBookingLink(lang, lang === 'hi' ? `मेरा सवाल था: ${getQ(f)}` : `My question: ${getQ(f)}`)}
                          target="_blank" rel="noopener noreferrer"
                          onClick={() => { trackConsultationCTAClick('faq_inline_cta', { faq: f.slug, category: f.category }); trackWhatsAppClick('faq_inline_cta'); }}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-gold to-amber-500 text-purple-950 font-bold text-sm shadow-lg hover:scale-105 active:scale-95 transition-transform whitespace-nowrap self-start sm:self-auto"
                          data-testid={`faq-consult-cta-${f.slug}`}>
                          <MessageCircle className="w-4 h-4" />
                          {lang === 'hi' ? 'WhatsApp पर बात करें' : 'Talk on WhatsApp'}
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Premium Authority + Booking CTA Block */}
      <section className="container-custom max-w-4xl pb-16">
        <ConsultationCTA lang={lang} />
      </section>

      <RelatedTools />
      <Footer />

      {/* Sticky Floating Booking CTA */}
      <a
        href={buildBookingLink(lang)}
        target="_blank" rel="noopener noreferrer"
        onClick={() => { trackConsultationCTAClick('faq_sticky'); trackWhatsAppClick('faq_sticky'); }}
        className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 z-50"
        data-testid="faq-sticky-cta">
        <span className="relative inline-flex">
          <span className="absolute inset-0 rounded-full bg-gold/40 animate-ping" />
          <span className="relative flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-gold to-amber-500 text-purple-950 font-bold text-sm shadow-2xl border-2 border-white/20 hover:scale-105 transition-transform">
            <MessageCircle className="w-4 h-4" />
            <span className="hidden sm:inline">{lang === 'hi' ? 'Personal Consultation' : 'Book Consultation'}</span>
            <span className="sm:hidden">{lang === 'hi' ? 'Book करें' : 'Book'}</span>
            <span className="hidden md:inline-block px-2 py-0.5 rounded-full bg-purple-950 text-gold text-[10px] font-bold ml-1">₹5,100</span>
          </span>
        </span>
      </a>
    </div>
  );
}

function CategoryPill({ active, onClick, icon, label, count, testId }) {
  return (
    <button onClick={onClick} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${active ? 'bg-gold/20 text-gold border border-gold/40' : 'bg-white/5 text-text-muted border border-white/10 hover:border-gold/20'}`} data-testid={testId}>
      <span>{icon}</span>
      <span>{label}</span>
      <span className={`px-2 py-0.5 rounded-full text-[10px] ${active ? 'bg-gold/30' : 'bg-white/10'}`}>{count}</span>
    </button>
  );
}

function ConsultationCTA({ lang, contextNote = '' }) {
  return (
    <div className="relative overflow-hidden rounded-3xl border border-gold/40 bg-gradient-to-br from-purple-950/60 via-amber-950/30 to-purple-950/60 p-6 sm:p-10 backdrop-blur-sm" data-testid="faq-trust-block">
      <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-gold/10 rounded-full blur-3xl" />
      <div className="relative grid sm:grid-cols-2 gap-6 items-center">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-gold font-bold mb-3">🕉️ {lang === 'hi' ? 'पराशर शास्त्र विशेषज्ञ' : 'Parashari Shastra Expert'}</div>
          <h3 className="font-heading text-2xl sm:text-3xl text-text-main mb-3 leading-tight">
            {lang === 'hi' ? 'आपका सवाल अनूठा है?' : 'Have a unique question?'}
          </h3>
          <p className="text-text-muted text-sm sm:text-base leading-relaxed mb-4">
            {lang === 'hi'
              ? 'हर कुंडली अद्वितीय होती है। 33+ वर्षों के अनुभव के साथ आचार्य Vivek Mudgal आपकी जन्म कुंडली का गहन Parashari विश्लेषण करेंगे।'
              : 'Every chart is unique. With 33+ years of experience, Acharya Vivek Mudgal will provide a deep Parashari analysis of your birth chart.'}
          </p>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-3 py-1 bg-gold/10 border border-gold/30 rounded-full text-gold flex items-center gap-1"><BookOpen className="w-3 h-3" /> {lang === 'hi' ? 'पराशरीय आधारित' : 'Parashari-based'}</span>
            <span className="px-3 py-1 bg-gold/10 border border-gold/30 rounded-full text-gold flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> {lang === 'hi' ? '33,000+ परामर्श' : '33,000+ consultations'}</span>
          </div>
        </div>
        <div className="text-center sm:text-right">
          <div className="inline-block bg-gradient-to-br from-gold/20 to-orange-500/10 border border-gold/40 rounded-2xl p-5 mb-4">
            <div className="text-xs uppercase tracking-widest text-gold font-bold mb-1">{lang === 'hi' ? 'Premium Personal Consultation' : 'Premium Personal Consultation'}</div>
            <div className="text-3xl sm:text-4xl font-heading text-gold mb-1">₹5,100</div>
            <div className="text-xs text-text-muted">{lang === 'hi' ? 'विस्तृत Parashari कुंडली विश्लेषण' : 'Detailed Parashari Kundli Analysis'}</div>
          </div>
          <p className="text-xs text-amber-300 mb-4 font-semibold">
            🪔 {lang === 'hi' ? 'आज केवल 3 personal consultation slots उपलब्ध हैं' : 'Only 3 personal consultation slots available today'}
          </p>
          <a
            href={buildBookingLink(lang, contextNote)}
            target="_blank" rel="noopener noreferrer"
            onClick={() => { trackConsultationCTAClick('faq_trust_block'); trackWhatsAppClick('faq_trust_block'); }}
            className="btn-gold inline-flex items-center gap-2"
            data-testid="faq-trust-block-cta">
            <MessageCircle className="w-4 h-4" />
            {lang === 'hi' ? 'अभी Book करें' : 'Book Now'}
          </a>
        </div>
      </div>
    </div>
  );
}
